import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, BookOpen, PlayCircle, Trophy, Sparkles, MessageCircleQuestion } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useI18n } from "@/contexts/I18nContext";

export default function HelpCenter() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const rules = [
    { k: "r1", q: t("help.rules.q1"), a: t("help.rules.a1") },
    { k: "r2", q: t("help.rules.q2"), a: t("help.rules.a2") },
    { k: "r3", q: t("help.rules.q3"), a: t("help.rules.a3") },
    { k: "r4", q: t("help.rules.q4"), a: t("help.rules.a4") },
    { k: "r5", q: t("help.rules.q5"), a: t("help.rules.a5") },
  ];
  const scoring = [
    { k: "s1", q: t("help.scoring.q1"), a: t("help.scoring.a1") },
    { k: "s2", q: t("help.scoring.q2"), a: t("help.scoring.a2") },
    { k: "s3", q: t("help.scoring.q3"), a: t("help.scoring.a3") },
    { k: "s4", q: t("help.scoring.q4"), a: t("help.scoring.a4") },
  ];
  const faq = [
    { k: "f1", q: t("help.faq.q1"), a: t("help.faq.a1") },
    { k: "f2", q: t("help.faq.q2"), a: t("help.faq.a2") },
    { k: "f3", q: t("help.faq.q3"), a: t("help.faq.a3") },
    { k: "f4", q: t("help.faq.q4"), a: t("help.faq.a4") },
    { k: "f5", q: t("help.faq.q5"), a: t("help.faq.a5") },
  ];

  const filt = (items) =>
    q.trim() === "" ? items : items.filter((i) =>
      (i.q + " " + i.a).toLowerCase().includes(q.toLowerCase())
    );

  const Section = ({ icon: Icon, title, items, testId }) => (
    <Card className="border-border" data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
          <h2 className="font-display font-bold text-lg">{title}</h2>
        </div>
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">—</div>
        ) : (
          <Accordion type="single" collapsible className="w-full">
            {items.map((i) => (
              <AccordionItem value={i.k} key={i.k}>
                <AccordionTrigger className="text-sm text-left" data-testid={`help-q-${i.k}`}>{i.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed" data-testid={`help-a-${i.k}`}>
                  {i.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
            {t("help.badge")}
          </div>
          <h1 className="font-display font-extrabold text-5xl tracking-tight mt-2">
            {t("help.title")}
          </h1>
          <p className="mt-3 text-muted-foreground">{t("help.subtitle")}</p>

          <div className="mt-6 relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("help.searchPlaceholder")}
              className="h-12 pl-11"
              data-testid="help-search-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <QuickCard icon={PlayCircle} label={t("help.quick.tutorial")} onClick={() => navigate("/tutorial")} testId="help-quick-tut" />
          <QuickCard icon={Sparkles} label={t("help.quick.demo")} onClick={() => navigate("/demo")} testId="help-quick-demo" />
          <QuickCard icon={Trophy} label={t("help.quick.play")} onClick={() => navigate("/play")} testId="help-quick-play" />
          <QuickCard icon={MessageCircleQuestion} label={t("help.quick.coach")} onClick={() => window.dispatchEvent(new CustomEvent("globo:coach-open"))} testId="help-quick-coach" />
        </div>

        <div className="space-y-4">
          <Section icon={BookOpen} title={t("help.section.rules")} items={filt(rules)} testId="help-section-rules" />
          <Section icon={Trophy} title={t("help.section.scoring")} items={filt(scoring)} testId="help-section-scoring" />
          <Section icon={MessageCircleQuestion} title={t("help.section.faq")} items={filt(faq)} testId="help-section-faq" />
        </div>
      </div>
    </AppLayout>
  );
}

function QuickCard({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="p-4 rounded-xl border border-border bg-card hover:border-primary hover:bg-primary/5 transition-all text-left"
      data-testid={testId}
    >
      <Icon className="w-5 h-5 text-primary mb-2" />
      <div className="text-sm font-medium">{label}</div>
    </button>
  );
}
